import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import BridgeClubLayout from '../../components/BridgeClub/BridgeClubLayout';
import { bridgeClubAPI } from '../../services/bridgeClubAPI';
import { useLocalAccess } from '../../hooks/useLocalAccess';
import './LocalCart.css';

interface Product {
  id: number;
  name: string;
  brand: string;
  size_lbs: string;
  price: string;
  is_byoc?: boolean;
  category?: string;
}

interface CartItem {
  id: number;
  product: Product;
  quantity: number;
  line_total: string;
}

interface Cart {
  id: number;
  items: CartItem[];
  item_count: number;
  subtotal: string;
}

// Helper function to calculate card processing fee
const calculateCardFee = (subtotal: number): number => {
  return Math.round((subtotal * 0.029 + 0.30) * 100) / 100;
};

const LocalCart: React.FC = () => {
  const navigate = useNavigate();
  const { hasLocalAccess } = useLocalAccess();
  const [cart, setCart] = useState<Cart | null>(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState<number | null>(null);
  const [pickupMethod, setPickupMethod] = useState<'in_store' | 'locker'>('in_store');
  const [paymentMethod, setPaymentMethod] = useState<'card' | 'ach' | 'cash'>('card');
  const [notes, setNotes] = useState('');
  const [requestedPickupDate, setRequestedPickupDate] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [paymentPending, setPaymentPending] = useState(false);
  const [pendingOrderId, setPendingOrderId] = useState<number | null>(null);

  // Listen for Helcim Pay modal completion.
  //
  // Architectural note: this handler is a UX fast-path only. The
  // AUTHORITATIVE paid signal is the Gmail celery worker on the
  // backend (feedmill.tasks._handle_bc_payment), which parses the
  // Helcim receipt email that lands in redbridgepayments@gmail.com.
  // If we fail to record here, the parser will catch up within ~5 min
  // and flip the order to paid then. RecordHelcimPaymentView is
  // idempotent — if the parser wins the race, we no-op.
  //
  // Parse pattern mirrors PortalBilling.tsx — HelcimPay.js nests the
  // real transactionId / amount inside `eventMessage` as a JSON
  // string; reading top-level keys returns undefined.
  //
  // Post-payment UX: regardless of outcome (success, abort, close,
  // failure) we navigate to the order detail page with a state flag.
  // The detail page renders the right banner and runs check-payment-now
  // for success/unknown outcomes. Leaving the customer on the frozen
  // cart with items still listed was the live-test UX bug.
  const handleHelcimMessage = useCallback(async (event: MessageEvent) => {
    if (!event.origin.includes('helcim.com')) return;
    const data = event.data;
    if (!data || typeof data !== 'object') return;

    // Helcim nests the txn/amount one level deep in eventMessage as a
    // JSON string. Surface both shapes we've seen in production:
    //   {data: {data: {transactionId, amount, ...}}}
    //   {data: {transactionId, ...}}
    //   {response: {transactionId, ...}}
    let payload: Record<string, any> = data;
    if (typeof data.eventMessage === 'string') {
      try {
        const parsed = JSON.parse(data.eventMessage);
        const txn = parsed?.data?.data || parsed?.data || parsed?.response || parsed;
        if (txn && typeof txn === 'object') {
          payload = { ...data, ...txn };
        }
      } catch (e) {
        console.warn('Could not parse Helcim eventMessage:', e);
      }
    }

    const statusStr = String(
      payload.eventStatus || payload.status || payload.response?.status || ''
    ).toUpperCase();
    const nameStr = String(payload.eventName || '');
    const isSuccess =
      nameStr === 'helcim-pay-success' ||
      (nameStr.startsWith('helcim-pay-') && statusStr === 'SUCCESS') ||
      statusStr === 'APPROVED' ||
      statusStr === 'SUCCESS' ||
      statusStr === 'COMPLETED';
    const isACHPending = statusStr === 'PENDING';
    const isFailure =
      nameStr === 'helcim-pay-error' ||
      statusStr === 'DECLINED' ||
      statusStr === 'ERROR' ||
      statusStr === 'ABORTED';
    const isClose =
      nameStr === 'helcim-pay-close' || nameStr === 'helcim-pay-cancel';

    // Determine what to communicate to the order detail page. Default
    // 'unknown' covers the live-test case where the iframe posted a
    // close event with no status — order may or may not be paid; let
    // the polling + parser figure it out.
    let paymentOutcome: 'success' | 'failure' | 'unknown' = 'unknown';
    if (isSuccess || isACHPending) {
      paymentOutcome = 'success';
    } else if (isFailure) {
      paymentOutcome = 'failure';
    } else if (!isClose) {
      return; // unrelated message — Helcim emits several we ignore
    }

    setPaymentPending(false);

    // UX fast-path: record server-side with verified Helcim API call.
    // Best-effort only; the Gmail parser is the authoritative writer.
    if (paymentOutcome === 'success' && pendingOrderId) {
      const transactionId =
        payload.transactionId ||
        payload.transId ||
        payload.transaction_id ||
        payload.response?.transactionId;
      const amount =
        payload.amount || payload.response?.amount;
      if (transactionId) {
        try {
          await bridgeClubAPI.recordHelcimPayment(pendingOrderId, {
            transaction_id: String(transactionId),
            amount,
          });
        } catch (err) {
          // Don't fail the redirect — Gmail parser will catch up.
          console.error('record-helcim-payment failed:', err);
        }
      } else {
        console.warn(
          '[LocalCart] Helcim success without transactionId — relying on Gmail parser'
        );
      }
    }

    if (paymentOutcome === 'failure') {
      setError(data.error || data.message || 'Payment was declined. Please try again.');
    }

    // ALWAYS navigate — never leave the customer on the cart with the
    // (now server-cleared) items still rendered. State carries the
    // outcome so the detail page can render the right banner.
    if (pendingOrderId) {
      setCart(null); // clear stale cart from local state
      navigate(`/bridge-club/local-orders/${pendingOrderId}`, {
        state: { paymentOutcome },
      });
    }
  }, [pendingOrderId, navigate]);

  useEffect(() => {
    window.addEventListener('message', handleHelcimMessage);
    return () => window.removeEventListener('message', handleHelcimMessage);
  }, [handleHelcimMessage]);

  // Check if any item is bulk (1000+ lbs)
  const isBulkOrder = cart?.items.some(item => {
    const sizeLbs = parseFloat(item.product?.size_lbs || '0') || 40; // Default to 40lb if missing
    return item.quantity * sizeLbs >= 1000;
  }) || false;

  // Locker ineligible if cart has BYOC or bulk items
  const hasLockerIneligible = cart?.items.some(item =>
    item.product?.is_byoc || (item.quantity * (parseFloat(item.product?.size_lbs || '0') || 40)) >= 1000
  ) || false;

  // Reset to in_store if locker becomes ineligible
  useEffect(() => {
    if (hasLockerIneligible && pickupMethod === 'locker') {
      setPickupMethod('in_store');
    }
  }, [hasLockerIneligible, pickupMethod]);

  // Calculate minimum pickup date (7 days from now for bulk)
  const minPickupDate = new Date();
  minPickupDate.setDate(minPickupDate.getDate() + 7);
  const minPickupDateStr = minPickupDate.toISOString().split('T')[0];

  useEffect(() => {
    if (!hasLocalAccess) {
      navigate('/bridge-club');
      return;
    }
    loadCart();
  }, [hasLocalAccess, navigate]);

  const loadCart = async () => {
    try {
      setLoading(true);
      const data = await bridgeClubAPI.getLocalCart();
      setCart(data);
    } catch (err) {
      console.error('Failed to load cart:', err);
    } finally {
      setLoading(false);
    }
  };

  const updateQuantity = async (itemId: number, newQuantity: number) => {
    try {
      setUpdating(itemId);
      if (newQuantity <= 0) {
        const updatedCart = await bridgeClubAPI.removeLocalCartItem(itemId);
        setCart(updatedCart);
      } else {
        const updatedCart = await bridgeClubAPI.updateLocalCartItem(itemId, newQuantity);
        setCart(updatedCart);
      }
    } catch (err) {
      console.error('Failed to update cart:', err);
    } finally {
      setUpdating(null);
    }
  };

  const handleCheckout = async () => {
    if (!cart || cart.item_count === 0) return;

    // Validate bulk order has pickup date
    if (isBulkOrder && !requestedPickupDate) {
      setError('Bulk orders require a requested pickup date (7+ days out)');
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      const order = await bridgeClubAPI.createLocalOrder({
        pickupMethod,
        notes,
        requestedPickupDate: isBulkOrder ? requestedPickupDate : undefined,
        paymentMethod,
      });

      // If checkout token available, show embedded Helcim modal
      if (order.checkout_token && window.appendHelcimPayIframe) {
        setPendingOrderId(order.id);
        setPaymentPending(true);
        window.appendHelcimPayIframe(order.checkout_token);
      } else if (order.payment_link) {
        // Fallback: navigate to order detail (they can pay from there)
        navigate(`/bridge-club/local-orders/${order.id}`);
      } else {
        navigate(`/bridge-club/local-orders/${order.id}`);
      }
    } catch (err: any) {
      console.error('Failed to create order:', err);
      setError(err.message || 'Failed to create order. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!hasLocalAccess) {
    return null;
  }

  if (loading) {
    return (
      <BridgeClubLayout>
        <div className="local-cart">
          <div className="cart-loading">
            <div className="spinner"></div>
            <p>Loading cart...</p>
          </div>
        </div>
      </BridgeClubLayout>
    );
  }

  return (
    <BridgeClubLayout>
      <div className="local-cart">
        <div className="cart-header">
          <Link to="/bridge-club/shop" className="back-link">Back to Shop</Link>
          <h1>Your Cart</h1>
        </div>

        {!cart || cart.item_count === 0 ? (
          <div className="cart-empty">
            <p>Your cart is empty</p>
            <Link to="/bridge-club/shop" className="shop-link">
              Start Shopping
            </Link>
          </div>
        ) : (
          <>
            <div className="cart-items">
              {cart.items.map((item) => (
                <div key={item.id} className="cart-item">
                  <div className="item-info">
                    <span className="item-brand">{item.product?.brand || ''}</span>
                    <h3 className="item-name">{item.product?.name || 'Unknown Product'}</h3>
                    <span className="item-size">{item.product?.size_lbs || '40'} lbs</span>
                  </div>
                  <div className="item-controls">
                    <div className="quantity-control">
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity - 1)}
                        disabled={updating === item.id}
                      >
                        -
                      </button>
                      <span>{item.quantity}</span>
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity + 1)}
                        disabled={updating === item.id}
                      >
                        +
                      </button>
                    </div>
                    <span className="item-total">${item.line_total}</span>
                  </div>
                </div>
              ))}
            </div>

            <div className="cart-checkout">
              {/* Bulk Order Notice */}
              {isBulkOrder && (
                <div className="bulk-order-notice">
                  <strong>Bulk Order</strong>
                  <p>Orders of 1000+ lbs require a 7-day lead time for milling. Staff will confirm your mill date.</p>
                </div>
              )}

              <div className="pickup-options">
                <h3>Pickup Method</h3>
                <label className="pickup-option">
                  <input
                    type="radio"
                    name="pickup"
                    value="in_store"
                    checked={pickupMethod === 'in_store'}
                    onChange={() => setPickupMethod('in_store')}
                  />
                  <div className="payment-info">
                    <span className="payment-label">In-Store Pickup</span>
                    <span className="payment-desc">Ready for pickup during store hours</span>
                  </div>
                </label>
                <label className={`pickup-option${hasLockerIneligible ? ' disabled' : ''}`}
                  title={hasLockerIneligible ? 'Locker not available for bulk/BYOC items' : ''}>
                  <input
                    type="radio"
                    name="pickup"
                    value="locker"
                    checked={pickupMethod === 'locker'}
                    onChange={() => !hasLockerIneligible && setPickupMethod('locker')}
                    disabled={hasLockerIneligible}
                  />
                  <div className="payment-info">
                    <span className="payment-label">24/7 Locker Pickup</span>
                    <span className="payment-desc">Pick up after hours or on weekends</span>
                    {!hasLockerIneligible && (
                      <span className="payment-desc" style={{ fontSize: '0.75rem', marginTop: '0.15rem' }}>
                        Same day pickup Tue-Fri if ordered before 4pm. Orders held 2 business days (3 days over weekends).
                      </span>
                    )}
                    {hasLockerIneligible && (
                      <span className="payment-desc" style={{ color: '#c53030', fontSize: '0.75rem' }}>
                        Locker not available for bulk/BYOC items
                      </span>
                    )}
                  </div>
                </label>
              </div>

              {/* Pickup Date for Bulk Orders */}
              {isBulkOrder && (
                <div className="pickup-date-section">
                  <label htmlFor="pickupDate">Requested Pickup Date</label>
                  <input
                    type="date"
                    id="pickupDate"
                    value={requestedPickupDate}
                    onChange={(e) => setRequestedPickupDate(e.target.value)}
                    min={minPickupDateStr}
                    required
                  />
                  <p className="date-hint">Earliest available: {new Date(minPickupDateStr).toLocaleDateString()}</p>
                </div>
              )}

              {/* Payment Method */}
              <div className="payment-options">
                <h3>Payment Method</h3>
                {/* Show ACH option for orders $100+ */}
                {parseFloat(cart.subtotal) >= 100 && (
                  <label className="payment-option">
                    <input
                      type="radio"
                      name="payment"
                      value="ach"
                      checked={paymentMethod === 'ach'}
                      onChange={() => setPaymentMethod('ach')}
                    />
                    <div className="payment-info">
                      <span className="payment-label">Bank Transfer (ACH) - No fee</span>
                      <span className="payment-desc">Ready when payment clears (1-3 business days)</span>
                    </div>
                  </label>
                )}
                <label className="payment-option">
                  <input
                    type="radio"
                    name="payment"
                    value="card"
                    checked={paymentMethod === 'card'}
                    onChange={() => setPaymentMethod('card')}
                  />
                  <div className="payment-info">
                    <span className="payment-label">Credit/Debit Card</span>
                    <span className="payment-desc">
                      {parseFloat(cart.subtotal) >= 100
                        ? `+ $${calculateCardFee(parseFloat(cart.subtotal)).toFixed(2)} processing fee - Ready tomorrow`
                        : 'Ready for pickup tomorrow'}
                    </span>
                  </div>
                </label>
                <label className="payment-option">
                  <input
                    type="radio"
                    name="payment"
                    value="cash"
                    checked={paymentMethod === 'cash'}
                    onChange={() => setPaymentMethod('cash')}
                  />
                  <div className="payment-info">
                    <span className="payment-label">Pay Cash at Pickup</span>
                    <span className="payment-desc">Pay when you pick up your order</span>
                  </div>
                </label>
                {/* ACH Savings Callout - show when card is selected and order is $100+ */}
                {paymentMethod === 'card' && parseFloat(cart.subtotal) >= 100 && (
                  <div className="savings-callout">
                    <span className="savings-icon">💡</span>
                    <div className="savings-text">
                      <strong>Save ${calculateCardFee(parseFloat(cart.subtotal)).toFixed(2)} with bank transfer!</strong>
                      <span>Order ready when payment clears (typically 1-3 business days)</span>
                    </div>
                  </div>
                )}
              </div>

              <div className="order-notes">
                <label htmlFor="notes">Order Notes (optional)</label>
                <textarea
                  id="notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Special instructions..."
                  rows={3}
                />
              </div>

              <div className="cart-summary">
                <div className="summary-row">
                  <span>Subtotal</span>
                  <span>${cart.subtotal}</span>
                </div>
                {paymentMethod === 'card' && parseFloat(cart.subtotal) >= 100 && (
                  <div className="summary-row">
                    <span>Card Processing Fee</span>
                    <span>${calculateCardFee(parseFloat(cart.subtotal)).toFixed(2)}</span>
                  </div>
                )}
                <div className="summary-row">
                  <span>Tax (8.0%)</span>
                  <span>${(
                    (parseFloat(cart.subtotal) +
                    (paymentMethod === 'card' && parseFloat(cart.subtotal) >= 100 ? calculateCardFee(parseFloat(cart.subtotal)) : 0)) * 0.08
                  ).toFixed(2)}</span>
                </div>
                <div className="summary-row summary-total">
                  <span>Total</span>
                  <span>
                    ${(
                      (parseFloat(cart.subtotal) +
                      (paymentMethod === 'card' && parseFloat(cart.subtotal) >= 100 ? calculateCardFee(parseFloat(cart.subtotal)) : 0)) * 1.08
                    ).toFixed(2)}
                  </span>
                </div>
              </div>

              {error && <div className="checkout-error">{error}</div>}

              <button
                className="checkout-btn"
                onClick={handleCheckout}
                disabled={submitting || (isBulkOrder && !requestedPickupDate)}
              >
                {submitting ? 'Processing...' : paymentMethod === 'card' ? 'Continue to Payment' : 'Place Order'}
              </button>
            </div>
          </>
        )}
      </div>
    </BridgeClubLayout>
  );
};

export default LocalCart;
